/**
 * Hook para integração do monitoramento de objetos com o React
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { monitoradorObjetos, MonitoradorObjetos } from '../../../lib/corel/MonitoradorObjetos';
import { logger, sendToTerminal, sendToTestLog } from '../../../lib/terminalLogger';
import { LogCategory } from '@/config/logging';
import { abortManager } from '../../../lib/corel/pipeline/abortManager';
import { validateOrderIds } from '../../../lib/corel/pipeline/idValidators';
import { MONITOR_EVENT_THRESHOLDS } from '../../../lib/corel/pipeline/pipelineConstants';

interface UseMonitoramentoObjetosProps {
  onObjetoAlterado?: (dados: any) => void;
  onObjetoCriado?: (dados: any) => void;
  onObjetoRemovido?: (dados: any) => void;
  // Novos callbacks para bloco unificado
  onObjetoAtualizar?: (objeto: any) => void; // recebe data.objeto
  onObjetoRemover?: (staticId: string) => void; // recebe objeto.staticId
  // Evento de documento
  onDocumento?: (dados: any) => void;
  onDocumentoFechado?: () => void;
  // Fornecedor tardio do arquivo monitorado (path/name). Avaliado no momento do evento
  getArquivoMonitorado?: () => { path?: string; name?: string; orcId?: number; oitId?: number };
  // Novo: adiar inicialização automática (status → iniciar → snapshot)
  autoInit?: boolean;
  // Novos: configuração adaptativa
  configuracao?: {
    pollingInterval?: number;
    batchSize?: number;
    snapshotPaginado?: boolean;
    snapshotPageSize?: number;
  };
  tamanhoDocumento?: 'pequeno' | 'medio' | 'grande';
}

export const useMonitoramentoObjetos = ({
  onObjetoAlterado,
  onObjetoCriado,
  onObjetoRemovido,
  onObjetoAtualizar,
  onObjetoRemover,
  onDocumento,
  onDocumentoFechado,
  getArquivoMonitorado,
  autoInit = true,
  configuracao,
  tamanhoDocumento
}: UseMonitoramentoObjetosProps = {}) => {
  const [isMonitorando, setIsMonitorando] = useState(false);
  const [webViewInicializado, setWebViewInicializado] = useState(false);
  const [docKey, setDocKey] = useState<string | null>(null);
  const [pinned, setPinned] = useState<boolean>(true);
  // ✅ Rastrear quando o monitor foi iniciado para ignorar eventos atrasados
  const [monitorIniciadoEm, setMonitorIniciadoEm] = useState<number | null>(null);
  // ✅ Cache de notificações processadas (evitar duplicatas - TTL: 3 segundos)
  const processedNotificationsRef = useRef<Set<string>>(new Set());
  const [paused, setPaused] = useState<boolean>(false);
  const [estatisticas, setEstatisticas] = useState({
    handlers: {
      alterado: 0,
      removido: 0,
      criado: 0,
      total: 0
    }
  });

  // Handler para alterações em objetos
  const handleObjetoAlterado = useCallback((dados: any) => {
    if (onObjetoAlterado) onObjetoAlterado(dados);
  }, [onObjetoAlterado]);

  // Handler para objetos criados
  const handleObjetoCriado = useCallback((dados: any) => {
    // Silencioso para reduzir ruído
    if (onObjetoCriado) onObjetoCriado(dados);
  }, [onObjetoCriado]);

  // Handler para objetos removidos
  const handleObjetoRemovido = useCallback((dados: any) => {
    // Silencioso para reduzir ruído
    if (onObjetoRemovido) onObjetoRemovido(dados);
  }, [onObjetoRemovido]);

  // ✅ IMPORTANTE: Handler de documento definido ANTES do useEffect
  // Isso garante que o handler esteja disponível mesmo se eventos chegarem durante a montagem
  const handleDocumentoRef = useRef<((dados: any) => void) | null>(null);
  
  // Handler genérico para todas as alterações
  const handleQualquerAlteracao = useCallback((dados: any) => {
    // Evento de status do documento: não contabilizar como alteração de objeto
    if (dados?.tipo === 'documento') {
      return;
    }

    // Suporte ao bloco unificado simplificado
    if (dados?.categoria === 'objeto') {
      const acao = String(dados?.acao || '').toLowerCase();
      const staticId = String(dados?.objeto?.staticId ?? dados?.staticId ?? '');
      
      // ✅ Backend envia: "alterado" | "criado" | "removido" (conforme NOTIFICACOES_MONITORAR_OBJETOS.md)
      // ✅ IMPORTANTE: Notificações já vêm com TODOS os dados (guid, multiplicador, contracao, etc.)
      //    Não é necessário chamar coletarMetadados - usar dados da notificação diretamente
      if ((acao === 'alterado' || acao === 'atualizar') && onObjetoAtualizar) {
        // ✅ Usar dados da notificação diretamente (já contém todos os metadados)
        try { 
          onObjetoAtualizar(dados?.objeto ?? dados); 
        } catch (e) { 
          logger.error('[MONITOR] onObjetoAtualizar error', { error: e instanceof Error ? e.message : String(e) }); 
        }
      } else if ((acao === 'criado' || acao === 'criar') && onObjetoAtualizar) {
        // ✅ Usar dados da notificação diretamente (já contém todos os metadados)
        try { 
          onObjetoAtualizar(dados?.objeto ?? dados); 
        } catch (e) { 
          logger.error('[MONITOR] onObjetoCriado error', { error: e instanceof Error ? e.message : String(e) }); 
        }
      } else if ((acao === 'removido' || acao === 'remover') && onObjetoRemover) {
        if (staticId) {
          // Objeto removido - silencioso
          try { onObjetoRemover(staticId); } catch (e) { logger.error('[MONITOR] onObjetoRemover error', { error: e instanceof Error ? e.message : String(e) }); }
        }
      }
    }
    
    // ✅ CORREÇÃO: Mapear acao → tipo para estatísticas
    // Backend envia: "alterado" | "criado" | "removido" (padrão atual)
    // Também aceita legado: "atualizar" | "criar" | "remover"
    let tipoParaEstatistica: 'alterado' | 'criado' | 'removido' | null = null;
    
    if (dados?.categoria === 'objeto') {
      const acao = String(dados?.acao || '').toLowerCase();
      // Mapear acao do C# para tipo das estatísticas
      // Backend envia: "alterado" | "criado" | "removido" (padrão atual)
      if (acao === 'alterado' || acao === 'atualizar') {
        tipoParaEstatistica = 'alterado';
      } else if (acao === 'criado' || acao === 'criar') {
        tipoParaEstatistica = 'criado';
      } else if (acao === 'removido' || acao === 'remover') {
        tipoParaEstatistica = 'removido';
      }
    } else if (dados?.tipo) {
      // Fallback: se já vier com tipo (compatibilidade com formato antigo)
      const tipo = String(dados.tipo).toLowerCase();
      if (tipo === 'alterado' || tipo === 'criado' || tipo === 'removido') {
        tipoParaEstatistica = tipo as 'alterado' | 'criado' | 'removido';
      }
    }
    
    // Atualizar estatísticas apenas se identificamos um tipo válido
    if (tipoParaEstatistica) {
      setEstatisticas(prev => ({
        handlers: {
          ...prev.handlers,
          [tipoParaEstatistica!]: (prev.handlers[tipoParaEstatistica!] || 0) + 1,
          total: prev.handlers.total + 1
        }
      }));
    }
  }, [onObjetoAtualizar, onObjetoRemover]);

  // ✅ Ref para rastrear montagens e detectar StrictMode
  const mountCountRef = useRef(0);
  const cleanupCountRef = useRef(0);

  // Verificar status inicial (retorna o objeto de status para evitar chamadas repetidas)
  const verificarStatusInicial = useCallback(async (): Promise<any | null> => {
    try {
      const status = await monitoradorObjetos.verificarStatus();
      if (status) {
        setWebViewInicializado(true);
        setIsMonitorando(Boolean(status.ativo));
        return status;
      }
      return null;
    } catch (error) {
      logger.error('[MONITOR] Erro ao verificar status inicial', { error: error instanceof Error ? error.message : String(error) });
      return null;
    }
  }, []);

  // Inicializar monitoramento (evitar chamadas repetidas de status)
  const inicializar = useCallback(async () => {
    try {
      // 1) Verificar status apenas uma vez
      const status = await verificarStatusInicial();

      // 2) ✅ NÃO reiniciar se já estiver ativo
      if (status && status.ativo) {
        // ✅ IMPORTANTE: Com pipeline unificada, o monitor já está ativo
        // Precisamos obter o docKey do MonitoradorObjetos se disponível
        // Isso garante que o hook tenha o docKey para validação quando o documento for fechado
        try {
          const monitoradorDocKey = monitoradorObjetos.getCurrentDocKey();
          if (monitoradorDocKey && typeof monitoradorDocKey === 'string') {
            setDocKey(monitoradorDocKey);
            logger.info('[MONITOR:HOOK] docKey obtido do MonitoradorObjetos (monitor já ativo)', {
              docKey: monitoradorDocKey
            });
          }
        } catch (err) {
          // Silencioso - não é crítico
        }
        return;
      }

      // 3) Decidir iniciar com base no status recebido
      const documentoDisponivel = (status && (status.documentoDisponivel != null
        ? Boolean(status.documentoDisponivel)
        : (typeof status.documentoAtivo === 'string' && status.documentoAtivo.toLowerCase() !== 'nenhum')));

      if (status && !status.ativo && documentoDisponivel) {
        // ✅ Usar configuração adaptativa ou calcular baseada no tamanho
        let config = configuracao;
        
        if (!config) {
          // Fallback: calcular baseado em tamanhoDocumento
          if (tamanhoDocumento === 'grande') {
            config = { pollingInterval: 400, batchSize: 15, snapshotPaginado: true };
          } else if (tamanhoDocumento === 'medio') {
            config = { pollingInterval: 300, batchSize: 5, snapshotPaginado: true };
          } else {
            // pequeno ou undefined: configuração leve
            config = { pollingInterval: 200, batchSize: 1, snapshotPaginado: false };
          }
        }
        
        const sucesso = await monitoradorObjetos.iniciarComConfiguracao(config);
        if (sucesso) {
          setIsMonitorando(true);
          // ✅ Registrar timestamp de início para ignorar eventos atrasados
          setMonitorIniciadoEm(Date.now());
        }
      }

      // 4) Solicitar snapshot após decidir/garantir monitoramento (evita fallback de status antes)
      try {
        const snap = await monitoradorObjetos.snapshot();
        if (snap) {
          // snapshot obtido
        }
      } catch {}
      // Monitor inicializado - silencioso
    } catch (error) {
      logger.error('[MONITOR] Erro ao inicializar monitoramento', { error: error instanceof Error ? error.message : String(error) });
    }
  }, [verificarStatusInicial, configuracao, tamanhoDocumento]); // Removido isMonitorando das dependências

  // Registrar handlers
  useEffect(() => {
    // ✅ Incrementar contador de montagens
    mountCountRef.current += 1;
    const currentMountCount = mountCountRef.current;
    
    // ✅ GERENCIAMENTO SIMPLES - Monitor inicia na pipeline, para ao fechar OrderDetailsWindow
    // Sem sistema de referências: apenas controle direto de início/parada
    
    // 🔍 LOG CRÍTICO: Stack trace do mount
    const mountStack = new Error().stack?.split('\n').slice(1, 15) || [];
    const mountStackInfo = mountStack
      .map(line => line.trim())
      .filter(line => !line.includes('useMonitoramentoObjetos.ts'))
      .slice(0, 8)
      .join(' → ');
    
    // 🔍 Detectar se é StrictMode de forma segura
    let isStrictMode = false;
    let reactVersion = '?';
    try {
      reactVersion = (React as any)?.version || '?';
      isStrictMode = typeof (React as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED !== 'undefined';
    } catch (e) {
      // React não disponível, ignorar
    }
    
    // ✅ Log reduzido - apenas primeira montagem
    if (currentMountCount === 1) {
      sendToTerminal('ℹ️ [INFO] [MONITOR:HOOK] OrderDetailsWindow montado', {
        hook: 'useMonitoramentoObjetos',
        timestamp: new Date().toISOString(),
        autoInit
      });
    }
    
    // ✅ Log removido - já logado acima (reduzir duplicação)
    
    // Registrar handlers específicos
    if (onObjetoAlterado) {
      monitoradorObjetos.on('alterado', handleObjetoAlterado);
    }
    
    if (onObjetoCriado) {
      monitoradorObjetos.on('criado', handleObjetoCriado);
    }
    
    if (onObjetoRemovido) {
      monitoradorObjetos.on('removido', handleObjetoRemovido);
    }
    
    // Registrar handler genérico para estatísticas
    monitoradorObjetos.on('*', handleQualquerAlteracao);
    
    // ✅ IMPORTANTE: Criar handler de documento e armazenar em ref
    // Isso garante que o handler seja estável mesmo durante remontagens do Strict Mode
    const handleDocumento = (dados: any) => {
      // 🛑 BLOQUEIO: Se monitor parado para salvamento, ignorar TODOS os eventos
      // Esta verificação é redundante (handlers já foram limpos), mas é uma segurança extra
      if (monitoradorObjetos.isStoppedForSave()) {
        // Silencioso - não logar para evitar ruído
        return;
      }

      // ✅ LOG CRÍTICO: Verificar se handler está sendo chamado
      logger.info('[MONITOR:HOOK] handleDocumento chamado', {
        tipo: dados?.tipo,
        status: dados?.status,
        docKey: dados?.docKey,
        monitoramentoParado: dados?.monitoramentoParado,
        timestamp: dados?.timestamp
      });

      if (dados?.tipo !== 'documento') {
        logger.warn('[MONITOR:HOOK] Tipo incorreto, ignorando', { tipo: dados?.tipo });
        return;
      }
      
      try {
        if (typeof dados?.docKey === 'string') setDocKey(String(dados.docKey));
        if (typeof dados?.pinned === 'boolean') setPinned(Boolean(dados.pinned));
      } catch {}
      try { onDocumento?.(dados); } catch {}
      // Pausado: monitor fixado em outro documento
      if (dados?.status === 'pausado') {
        setPaused(true);
        return;
      }
      // Retomou documento fixado
      if (dados?.status === 'trocado') {
        setPaused(false);
        (async () => {
          try {
            const snap = await monitoradorObjetos.snapshot();
            if (snap) {
              // silencioso: snapshot após troca de documento
            }
          } catch {}
        })();
      }
      // ✅ Documento fechado: validar status OU monitoramentoParado
      // Backend envia monitoramentoParado: true quando status === "fechado" ou status === "trocado"
      const isDocumentClosed = dados?.status === 'fechado' || dados?.monitoramentoParado === true;

      if (isDocumentClosed) {
        // 🛑 NOTA: A proteção contra eventos durante salvamento agora é feita via:
        // 1. stopCompletelyForSave() - limpa handlers e marca flag
        // 2. isStoppedForSave() - verificação no início deste handler
        // 3. processarNotificacao() - bloqueio no MonitoradorObjetos
        // Não precisamos mais verificar hasCriticalOperationActive aqui!

        logger.info('[MONITOR] DOCUMENTO_FECHADO', { 
          status: dados?.status,
          monitoramentoParado: dados?.monitoramentoParado,
          docKey: dados?.docKey,
          arquivoAnterior: dados?.arquivoAnterior,
          arquivoAtual: dados?.arquivoAtual,
          timestamp: dados?.timestamp
        });
        
        try {
          // ✅ VALIDAÇÃO: Ignorar notificações duplicadas (mesmo docKey + status + timestamp)
          const notificationKey = `${dados?.docKey || 'unknown'}:fechado:${dados?.timestamp || 'no-timestamp'}`;
          if (processedNotificationsRef.current.has(notificationKey)) {
            logger.info('[MONITOR] Notificação duplicada ignorada', {
              notificationKey,
              docKey: dados?.docKey,
              timestamp: dados?.timestamp
            });
            return;
          }
          
          // ✅ Marcar como processada e limpar após 3 segundos (TTL)
          processedNotificationsRef.current.add(notificationKey);
          setTimeout(() => {
            processedNotificationsRef.current.delete(notificationKey);
          }, 3000);
          
          // ✅ Proteção contra eventos atrasados: ignorar se monitor foi iniciado recentemente (< 5s)
          const agora = Date.now();
          // ✅ CORRIGIDO: Parse timestamp UTC (formato "YYYY-MM-DD HH:mm:ss" → ISO 8601)
          let eventoTimestamp = agora;
          if (dados?.timestamp && typeof dados.timestamp === 'string') {
            try {
              // Converter "2025-12-19 03:30:56" → "2025-12-19T03:30:56Z" (UTC)
              const isoString = dados.timestamp.replace(' ', 'T') + 'Z';
              eventoTimestamp = new Date(isoString).getTime();
            } catch (err) {
              logger.warn('[MONITOR] Erro ao parsear timestamp', {
                timestamp: dados.timestamp,
                error: err instanceof Error ? err.message : String(err)
              });
              eventoTimestamp = agora;
            }
          }
          const tempoDesdeInicio = monitorIniciadoEm ? agora - monitorIniciadoEm : Infinity;
          const tempoDesdeEvento = agora - eventoTimestamp;
          
          // Ignorar eventos antigos ou se monitor foi iniciado muito recentemente
          const eventoMuitoAntigo = tempoDesdeEvento > MONITOR_EVENT_THRESHOLDS.OLD_EVENT_MS;
          const monitorIniciadoRecentemente = tempoDesdeInicio < 5000; // ✅ Aumentado para 5s (alinhado com MonitoradorObjetos)
          
          // ✅ VALIDAÇÃO ADICIONAL: Se não temos docKey ainda, mas passou o período de estabilização, 
          // ainda podemos processar se houver match por nome de arquivo
          const semDocKeyAinda = !docKey || docKey === 'unknown';
          
          // ✅ IMPORTANTE: Se passou o período de estabilização (> 5s), processar mesmo sem docKey
          // O match por nome de arquivo pode funcionar
          const passouPeriodoEstabilizacao = tempoDesdeInicio >= 5000;
          
          if (eventoMuitoAntigo || (monitorIniciadoRecentemente && semDocKeyAinda && !passouPeriodoEstabilizacao)) {
            logger.info('[MONITOR] Evento "fechado" ignorado (proteção)', {
              motivo: eventoMuitoAntigo ? 'evento_antigo' : 'monitor_recente_sem_dockey',
              tempoDesdeInicio,
              tempoDesdeEvento,
              temDocKey: !semDocKeyAinda,
              docKeyAtual: docKey,
              passouPeriodoEstabilizacao
            });
            return;
          }
          
          const getBaseName = (p?: string | null) => {
            if (!p || typeof p !== 'string') return '';
            const parts = p.split(/\\|\//);
            return parts[parts.length - 1]?.toLowerCase?.() || '';
          };
          
          const monitored = getArquivoMonitorado?.() || {};
          const currentKey = (monitored.path && getBaseName(monitored.path))
            || (monitored.name && monitored.name.toLowerCase())
            || '';
          const closedKey = getBaseName(dados?.arquivoAnterior) || getBaseName(dados?.arquivoAtual);
          
          // ✅ Validação: PRIORIZAR docKey (mais confiável que nome de arquivo)
          // Para documentos UNNAMED, arquivoAnterior pode ser "" mas docKey sempre está presente
          const eventDocKey = dados?.docKey;
          const stateDocKey = docKey; // Estado atual (pode estar desatualizado)
          
          // ✅ IMPORTANTE: Se o evento tem docKey e monitoramentoParado: true, 
          // isso indica que o backend parou o monitoramento para este documento específico
          // Neste caso, podemos confiar no docKey do evento mesmo se o estado não tiver docKey
          const hasValidEventDocKey = eventDocKey && eventDocKey !== 'unknown' && eventDocKey !== '';
          const monitoramentoParado = dados?.monitoramentoParado === true;
          
          // ✅ Validação de docKey: comparar evento com estado OU aceitar evento se monitoramentoParado
          // Se monitoramentoParado: true, o backend confirma que é o documento correto
          const docKeyMatch = hasValidEventDocKey && stateDocKey && eventDocKey === stateDocKey;
          const docKeyMatchFromEvent = hasValidEventDocKey && monitoramentoParado && (!stateDocKey || eventDocKey === stateDocKey);
          
          // ✅ Validação por nome de arquivo (fallback quando docKey não está disponível)
          const nomeMatch = currentKey && closedKey && currentKey === closedKey;
          
          // ✅ IMPORTANTE: Se estado docKey está null mas evento tem docKey válido e monitoramentoParado,
          // aceitar como válido (backend confirma que é o documento correto)
          const hasEventDocKeyButNoState = hasValidEventDocKey && !stateDocKey && monitoramentoParado;
          const fallbackMatch = hasEventDocKeyButNoState || (hasValidEventDocKey && !stateDocKey && nomeMatch);
          
          // ✅ IMPORTANTE: Se arquivoAnterior está vazio (UNNAMED), usar apenas docKey
          const isUnnamed = !dados?.arquivoAnterior || dados?.arquivoAnterior === '';
          
          // ✅ LÓGICA CORRIGIDA: Fechar se:
          // 1. docKey do evento corresponde ao estado (docKeyMatch)
          // 2. docKey do evento é válido E monitoramentoParado: true (docKeyMatchFromEvent)
          // 3. Nome do arquivo corresponde (nomeMatch) - apenas para documentos nomeados
          // 4. Fallback: evento tem docKey válido mas estado não tem (fallbackMatch)
          const shouldClose = isUnnamed 
            ? (docKeyMatch || docKeyMatchFromEvent || fallbackMatch)  // UNNAMED: docKey do evento OU estado
            : (docKeyMatch || docKeyMatchFromEvent || nomeMatch || fallbackMatch); // Documento nomeado: docKey OU nome OU fallback
          
          // 🔍 LOG CRÍTICO: Debug da validação de fechamento
          logger.info('[MONITOR:VALIDATION] Validando fechamento de documento', {
            // Estado atual
            docKeyAtual: docKey,
            currentKey,
            // Evento recebido
            docKeyEvento: dados?.docKey,
            closedKey,
            arquivoAnterior: dados?.arquivoAnterior,
            arquivoAtual: dados?.arquivoAtual,
            monitoramentoParado: dados?.monitoramentoParado,
            timestamp: dados?.timestamp,
            // Resultados da validação
            nomeMatch,
            docKeyMatch,
            docKeyMatchFromEvent,
            fallbackMatch,
            isUnnamed,
            shouldClose,
            // Timing
            tempoDesdeInicio: monitorIniciadoEm ? Date.now() - monitorIniciadoEm : 'N/A',
            tempoDesdeEvento
          });
          
          if (shouldClose) {
            // 🆕 Sinalizar abort para operações de salvamento ativas
            try {
              const monitored = getArquivoMonitorado?.() || {};
              const orcId = monitored.orcId;
              const oitId = monitored.oitId;
              
              const validation = validateOrderIds(orcId, oitId);
              
              if (validation.areBothValid && validation.operationId) {
                abortManager.abort(validation.operationId, 'document_closed', {
                  docKey: dados?.docKey,
                  arquivoAnterior: dados?.arquivoAnterior,
                  timestamp: dados?.timestamp
                });
                
                logger.info('[MONITOR] Abort sinalizado para salvamento', { 
                  operationId: validation.operationId,
                  reason: 'document_closed'
                });
              } else {
                logger.warn('[MONITOR] IDs inválidos para abort', {
                  orcId,
                  oitId,
                  isValidOrcId: validation.isValidOrcId,
                  isValidOitId: validation.isValidOitId
                });
              }
            } catch (err) {
              logger.warn('[MONITOR] Erro ao sinalizar abort', { 
                error: err instanceof Error ? err.message : String(err) 
              });
            }
            
            logger.info('[MONITOR] Documento fechado - fechando OrderDetails', {
              reason: nomeMatch ? 'nome_match' : docKeyMatch ? 'dockey_match' : 'monitorando'
            });
            onDocumentoFechado?.();
          } else {
            logger.info('[MONITOR] Evento de fechamento IGNORADO - documento diferente ou sem match', {
              motivo: 'shouldClose = false',
              docKeyMatch,
              nomeMatch,
              docKeyAtual: docKey,
              docKeyEvento: dados?.docKey,
              currentKey,
              closedKey,
              arquivoAnterior: dados?.arquivoAnterior,
              note: 'Evento de outro documento não afeta o salvamento atual'
            });
          }
        } catch (err) {
          logger.error('[MONITOR] ERROR_HANDLING_DOC_CLOSED', { 
            error: err instanceof Error ? err.message : String(err) 
          });
        }
      }
      
      // Monitoramento encerrado: confirma que recursos foram liberados
      if (dados?.status === 'encerrado') {
        // 🛑 NOTA: A proteção contra eventos durante salvamento agora é feita via:
        // 1. stopCompletelyForSave() - limpa handlers e marca flag
        // 2. isStoppedForSave() - verificação no início deste handler
        // Não precisamos mais verificar hasCriticalOperationActive aqui!

        logger.info('[MONITOR] MONITORAMENTO_ENCERRADO', { 
          docKey: dados?.docKey,
          arquivoAnterior: dados?.arquivoAnterior,
          arquivoAtual: dados?.arquivoAtual,
          timestamp: dados?.timestamp
        });
        
        try {
          // ✅ Proteção contra eventos atrasados: ignorar se monitor foi iniciado recentemente (< 2s)
          const agora = Date.now();
          const eventoTimestamp = dados?.timestamp ? new Date(dados.timestamp).getTime() : agora;
          const tempoDesdeInicio = monitorIniciadoEm ? agora - monitorIniciadoEm : Infinity;
          const tempoDesdeEvento = agora - eventoTimestamp;
          
          // Ignorar eventos antigos ou se monitor foi iniciado muito recentemente
          const eventoMuitoAntigo = tempoDesdeEvento > MONITOR_EVENT_THRESHOLDS.OLD_EVENT_MS;
          const monitorIniciadoRecentemente = tempoDesdeInicio < MONITOR_EVENT_THRESHOLDS.RECENT_INIT_MS;
          
          // 🔍 LOG CRÍTICO: Debug de timing
          logger.debug('[MONITOR:TIMING] Verificando timing do evento "encerrado"', {
            eventoMuitoAntigo,
            monitorIniciadoRecentemente,
            tempoDesdeEvento: `${tempoDesdeEvento}ms`,
            tempoDesdeInicio: `${tempoDesdeInicio}ms`,
            eventoTimestamp: new Date(eventoTimestamp).toISOString(),
            monitorIniciadoEm: monitorIniciadoEm ? new Date(monitorIniciadoEm).toISOString() : 'N/A',
            vaiIgnorar: eventoMuitoAntigo || monitorIniciadoRecentemente
          });
          
          if (eventoMuitoAntigo || monitorIniciadoRecentemente) {
            logger.warn('[MONITOR] Evento "encerrado" IGNORADO por timing', {
              motivo: eventoMuitoAntigo ? 'evento_muito_antigo' : 'monitor_iniciado_recentemente',
              tempoDesdeEvento: `${tempoDesdeEvento}ms`,
              tempoDesdeInicio: `${tempoDesdeInicio}ms`
            });
            // Não atualizar isMonitorando se for evento atrasado
            return;
          }
          
          // Atualizar estado: monitor não está mais ativo (só se não for evento atrasado)
          setIsMonitorando(false);
          
          const getBaseName = (p?: string | null) => {
            if (!p || typeof p !== 'string') return '';
            const parts = p.split(/\\|\//);
            return parts[parts.length - 1]?.toLowerCase?.() || '';
          };
          
          const monitored = getArquivoMonitorado?.() || {};
          const currentKey = (monitored.path && getBaseName(monitored.path))
            || (monitored.name && monitored.name.toLowerCase())
            || '';
          const closedKey = getBaseName(dados?.arquivoAnterior) || getBaseName(dados?.arquivoAtual);
          
          // ✅ Validação: PRIORIZAR docKey (mais confiável que nome de arquivo)
          // MESMA LÓGICA DO "FECHADO" para garantir consistência
          const nomeMatch = currentKey && closedKey && currentKey === closedKey;
          const docKeyMatch = dados?.docKey && docKey && dados.docKey === docKey;
          // Só não fecha se tiver CERTEZA que é outro arquivo (nome E docKey diferentes)
          const isDifferentDoc = currentKey && closedKey && currentKey !== closedKey && 
                                 dados?.docKey && docKey && dados.docKey !== docKey;
          // ✅ LÓGICA CORRIGIDA: Fechar APENAS se houver match positivo (docKey OU nome)
          // NÃO fechar se não houver informação suficiente ou se for outro documento
          // Isso evita abortar salvamentos quando o evento é de outro arquivo
          const shouldClose = docKeyMatch || nomeMatch;
          
          // 🔍 LOG CRÍTICO: Debug da validação de encerramento
          logger.debug('[MONITOR:VALIDATION] Validando encerramento de monitoramento', {
            // Estado atual
            docKeyAtual: docKey,
            currentKey,
            // Evento recebido
            docKeyEvento: dados?.docKey,
            closedKey,
            arquivoAnterior: dados?.arquivoAnterior,
            arquivoAtual: dados?.arquivoAtual,
            // Resultados da validação
            nomeMatch,
            docKeyMatch,
            isDifferentDoc,
            shouldClose,
            // Timing
            tempoDesdeInicio: monitorIniciadoEm ? Date.now() - monitorIniciadoEm : 'N/A'
          });
          
          if (shouldClose) {
            // 🆕 Sinalizar abort para operações de salvamento ativas
            try {
              const monitored = getArquivoMonitorado?.() || {};
              const orcId = monitored.orcId;
              const oitId = monitored.oitId;
              
              const validation = validateOrderIds(orcId, oitId);
              
              if (validation.areBothValid && validation.operationId) {
                abortManager.abort(validation.operationId, 'document_closed', {
                  docKey: dados?.docKey,
                  arquivoAnterior: dados?.arquivoAnterior,
                  timestamp: dados?.timestamp
                });
                
                logger.info('[MONITOR] Abort sinalizado para salvamento (encerrado)', { 
                  operationId: validation.operationId,
                  reason: 'document_closed'
                });
              } else {
                logger.warn('[MONITOR] IDs inválidos para abort (encerrado)', {
                  orcId,
                  oitId,
                  isValidOrcId: validation.isValidOrcId,
                  isValidOitId: validation.isValidOitId
                });
              }
            } catch (err) {
              logger.warn('[MONITOR] Erro ao sinalizar abort (encerrado)', { 
                error: err instanceof Error ? err.message : String(err) 
              });
            }
            
            logger.info('[MONITOR] Monitoramento encerrado - fechando OrderDetails', {
              reason: nomeMatch ? 'nome_match' : docKeyMatch ? 'dockey_match' : 'monitorando'
            });
            onDocumentoFechado?.();
          } else {
            logger.info('[MONITOR] Evento de encerramento IGNORADO - documento diferente ou sem match', {
              motivo: 'shouldClose = false',
              docKeyMatch,
              nomeMatch,
              docKeyAtual: docKey,
              docKeyEvento: dados?.docKey,
              currentKey,
              closedKey,
              arquivoAnterior: dados?.arquivoAnterior,
              note: 'Evento de outro documento não afeta o salvamento atual'
            });
          }
        } catch (err) {
          logger.error('[MONITOR] ERROR_HANDLING_MONITORING_ENDED', { 
            error: err instanceof Error ? err.message : String(err) 
          });
        }
      }
    };
    
    // ✅ IMPORTANTE: Armazenar handler em ref para garantir estabilidade
    handleDocumentoRef.current = handleDocumento;
    
    // ✅ IMPORTANTE: Registrar handler ANTES de qualquer outra coisa
    // Isso garante que eventos que chegam durante a montagem sejam capturados
    monitoradorObjetos.on('documento', handleDocumento);
    
    // ✅ LOG CRÍTICO: Confirmar que handler foi registrado
    sendToTerminal('✅ [MONITOR:HOOK] Handler "documento" REGISTRADO', {
      hook: 'useMonitoramentoObjetos',
      hasOnDocumentoFechado: !!onDocumentoFechado,
      mountCount: currentMountCount
    }, LogCategory.EVENT);
    
    // Inicializar monitoramento apenas uma vez (a menos que autoInit seja false)
    if (autoInit !== false) {
      inicializar();
    } else {
      logger.debug('[FLOW:MONITOR_INIT] DEFERRED');
    }
    
    // Cleanup
    return () => {
      // ✅ Incrementar contador de cleanups
      cleanupCountRef.current += 1;
      const currentCleanupCount = cleanupCountRef.current;
      const currentMountCountAtCleanup = mountCountRef.current;
      
      // ✅ LOG CRÍTICO: Confirmar que handler está sendo desregistrado
      sendToTerminal('🔌 [MONITOR:HOOK] Handler "documento" DESREGISTRADO', {
        hook: 'useMonitoramentoObjetos',
        mountCount: currentMountCountAtCleanup,
        cleanupCount: currentCleanupCount
      }, LogCategory.EVENT);
      
      // Remover handlers
      if (onObjetoAlterado) {
        monitoradorObjetos.off('alterado', handleObjetoAlterado);
      }
      
      if (onObjetoCriado) {
        monitoradorObjetos.off('criado', handleObjetoCriado);
      }
      
      if (onObjetoRemovido) {
        monitoradorObjetos.off('removido', handleObjetoRemovido);
      }
      
      monitoradorObjetos.off('*', handleQualquerAlteracao);
      
      // ✅ IMPORTANTE: Desregistrar usando o handler do ref (se disponível)
      if (handleDocumentoRef.current) {
        monitoradorObjetos.off('documento', handleDocumentoRef.current);
        handleDocumentoRef.current = null;
      }
      
      // ✅ PARAR MONITOR ao desmontar OrderDetailsWindow
      // 🔍 LOG CRÍTICO: Rastrear stack trace completo do cleanup
      const cleanupStack = new Error().stack?.split('\n').slice(1, 15) || [];
      const cleanupStackInfo = cleanupStack
        .map(line => line.trim())
        .filter(line => !line.includes('useMonitoramentoObjetos.ts'))
        .slice(0, 8)
        .join(' → ');
      
      // ✅ DETECÇÃO ROBUSTA: Primeira cleanup é sempre StrictMode (se houver)
      // - Se mountCount === 1 e cleanupCount === 1 → É primeira desmontagem = StrictMode
      // - Se cleanupCount > 1 → É desmontagem real
      const isFirstCleanupOfStrictMode = currentMountCountAtCleanup === 1 && currentCleanupCount === 1;
      
      // ✅ Log removido - reduzir verbosidade (cleanup é normal do React)
      
      // ✅ PROTEÇÃO: Não parar monitor na primeira cleanup (StrictMode)
      if (isFirstCleanupOfStrictMode) {
        // ✅ Log removido - reduzir verbosidade
        // NÃO parar o monitor - apenas removeu handlers acima
        return;
      }
      
      // Parar monitor de forma assíncrona (não esperar na cleanup) - APENAS SE NÃO FOR STRICTMODE
      monitoradorObjetos.pararMonitoramento().then((sucesso) => {
        sendToTerminal('✅ [MONITOR:HOOK] Monitor parado ao desmontar', {
          sucesso,
          hook: 'useMonitoramentoObjetos',
          timestamp: new Date().toISOString()
        }, LogCategory.EVENT);
        
        sendToTestLog('✅ [MONITOR:HOOK] Monitor parado ao desmontar', {
          sucesso,
          hook: 'useMonitoramentoObjetos',
          timestamp: new Date().toISOString()
        }, 'csharp', LogCategory.EVENT);
      }).catch((error) => {
        sendToTerminal('❌ [MONITOR:HOOK] Erro ao parar monitor no cleanup', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          hook: 'useMonitoramentoObjetos'
        }, LogCategory.ERROR);
      });
    };
  }, []); // Executar apenas uma vez na montagem do componente

  // Expor pageIndex/bbox/zOrder nos callbacks se forem necessários futuramente

  // useEffect separado para atualizar handlers quando props mudam
  useEffect(() => {
    // Remover handlers antigos
    if (onObjetoAlterado) {
      monitoradorObjetos.off('alterado', handleObjetoAlterado);
    }
    if (onObjetoCriado) {
      monitoradorObjetos.off('criado', handleObjetoCriado);
    }
    if (onObjetoRemovido) {
      monitoradorObjetos.off('removido', handleObjetoRemovido);
    }
    
    // Registrar handlers novos
    if (onObjetoAlterado) {
      monitoradorObjetos.on('alterado', handleObjetoAlterado);
    }
    if (onObjetoCriado) {
      monitoradorObjetos.on('criado', handleObjetoCriado);
    }
    if (onObjetoRemovido) {
      monitoradorObjetos.on('removido', handleObjetoRemovido);
    }
  }, [onObjetoAlterado, onObjetoCriado, onObjetoRemovido, handleObjetoAlterado, handleObjetoCriado, handleObjetoRemovido]);

  // Funções expostas
  const pararMonitoramento = useCallback(async () => {
    try {
      const sucesso = await monitoradorObjetos.pararMonitoramento();
      if (sucesso) {
        setIsMonitorando(false);
      }
      return sucesso;
    } catch (error) {
      logger.error('[MONITOR] Erro ao parar monitoramento', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }, []);

  const testarComunicacao = useCallback(async () => {
    try {
      return await monitoradorObjetos.testarComunicacao();
    } catch (error) {
      logger.error('[MONITOR] Erro ao testar comunicação', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }, []);

  const notificarWebViewDisponivel = useCallback(async () => {
    try {
      const sucesso = await monitoradorObjetos.notificarWebViewDisponivel();
      if (sucesso) {
        setWebViewInicializado(true);
      }
      return sucesso;
    } catch (error) {
      logger.error('[MONITOR] Erro ao notificar WebView', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }, []);

  // Novo: Sincronizar objetos
  const sincronizarObjetos = useCallback(async () => {
    try {
      return await monitoradorObjetos.sincronizarObjetos();
    } catch (error) {
      logger.error('[MONITOR] Erro ao sincronizar objetos', { error: error instanceof Error ? error.message : String(error) });
      return null;
    }
  }, []);

  // Novo: Iniciar com configuração adaptativa
  const iniciarComConfiguracao = useCallback(async (config?: any) => {
    try {
      const configFinal = config || configuracao || 
        (tamanhoDocumento ? MonitoradorObjetos.getConfiguracaoPorCenario(tamanhoDocumento) : undefined);
      
      if (configFinal) {
        return await monitoradorObjetos.iniciarComConfiguracao(configFinal);
      } else {
        return await inicializar();
      }
    } catch (error) {
      logger.error('[MONITOR] Erro ao iniciar com configuração', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }, [configuracao, tamanhoDocumento, inicializar]);

  return {
    isMonitorando,
    webViewInicializado,
    estatisticas,
    docKey,
    pinned,
    paused,
    pararMonitoramento,
    testarComunicacao,
    notificarWebViewDisponivel,
    inicializarMonitoramento: inicializar,
    // Novos métodos
    sincronizarObjetos,
    iniciarComConfiguracao
  };
};